import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Container, Card, CardBody, Spinner, Alert, Badge, Button,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input,
  Row, Col, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap";
import { toast } from "react-toastify";
import { useAuth } from "auth/useAuth";
import { setupApiAuth } from "../../helpers/auth-helper";
import ApiService from "../../shared/services/apiService";
import TableContainer from "../../components/Common/TableContainer";
import { generateTemporaryPassword, meetsPasswordPolicy, PASSWORD_POLICY_TEXT } from "../../shared/utils/password";
import config from "../../config/env";

const STATUS_LABELS = {
  CONFIRMED: "Ativo",
  UNCONFIRMED: "E-mail não confirmado",
  FORCE_CHANGE_PASSWORD: "Convite pendente",
  RESET_REQUIRED: "Redefinição pendente",
  DISABLED: "Desativado",
};

/** Copies text to the clipboard; resolves false when the API is unavailable. */
const copyToClipboard = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};

const STATUS_COLORS = {
  CONFIRMED: "success",
  UNCONFIRMED: "warning",
  FORCE_CHANGE_PASSWORD: "warning",
  RESET_REQUIRED: "warning",
  DISABLED: "danger",
};

const GROUP_COLORS = {
  admin: "danger",
  associates: "primary",
  guests: "info",
};

const GROUP_LABELS = {
  admin: "Administrador",
  associates: "Associados",
  guests: "Convidados",
};

const translateGroup = (name) => GROUP_LABELS[name] || name;

// Module-level cache
let cachedUsers = [];
let cachedGroups = [];
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const isCacheValid = () => cachedUsers.length > 0 && (Date.now() - cacheTimestamp) < CACHE_TTL;

const UsersList = () => {
  document.title = "Usuários | ACIG Guapiaçu Cidadão";

  const auth = useAuth();
  const [users, setUsers] = useState(cachedUsers);
  const [allGroups, setAllGroups] = useState(cachedGroups);
  const [loading, setLoading] = useState(!isCacheValid());
  const [error, setError] = useState(null);
  const [filterGroup, setFilterGroup] = useState("");

  // Modal state — groups
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Modal state — invite user
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteGivenName, setInviteGivenName] = useState("");
  const [inviteFamilyName, setInviteFamilyName] = useState("");
  const [inviteGroups, setInviteGroups] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [inviteDelivery, setInviteDelivery] = useState("email"); // email | manual
  const [invitePassword, setInvitePassword] = useState("");
  const [inviteHandoff, setInviteHandoff] = useState(null); // { email, password } shown once after manual creation

  // Modal state — access (temporary password / resend invite)
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [accessPassword, setAccessPassword] = useState("");
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [accessSuccess, setAccessSuccess] = useState(null);
  const [copied, setCopied] = useState(false);

  // Modal state — delete user
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const openDeleteModal = (user) => {
    setDeleteUser(user);
    setDeleteConfirmText("");
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const handleDeleteUser = async () => {
    try {
      setDeleting(true);
      setDeleteError(null);
      setupApiAuth(auth);
      await ApiService.deleteCognitoUser(deleteUser.username);
      setDeleteOpen(false);
      toast.success(`Usuário ${deleteUser.email || ""} excluído.`);
      fetchUsers(true);
    } catch (err) {
      console.error("Error deleting user:", err);
      setDeleteError(err.response?.data?.message || "Erro ao excluir usuário.");
    } finally {
      setDeleting(false);
    }
  };

  const fetchUsers = useCallback(async (force = false) => {
    if (isCacheValid() && !force) {
      setUsers(cachedUsers);
      setAllGroups(cachedGroups);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setupApiAuth(auth);
      const [usersData, groupsData] = await Promise.all([
        ApiService.listCognitoUsers(),
        ApiService.listCognitoGroups(),
      ]);
      const usersResult = usersData.data || usersData;
      const groupsResult = groupsData.data || groupsData;
      cachedUsers = usersResult;
      cachedGroups = groupsResult;
      cacheTimestamp = Date.now();
      setUsers(usersResult);
      setAllGroups(groupsResult);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(
        err.response?.status === 403
          ? "Acesso negado. Você não tem permissão para visualizar usuários."
          : "Erro ao carregar usuários."
      );
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    if (auth.isAuthenticated) fetchUsers();
  }, [auth.isAuthenticated]);

  const openGroupModal = (user) => {
    setSelectedUser(user);
    setSelectedGroups(user.groups || []);
    setSaveError(null);
    setModalOpen(true);
  };

  const toggleGroup = (groupName) => {
    setSelectedGroups((prev) =>
      prev.includes(groupName) ? prev.filter((g) => g !== groupName) : [...prev, groupName]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      setupApiAuth(auth);
      await ApiService.setUserGroups(selectedUser.username, selectedGroups);
      setUsers((prev) => {
        const updated = prev.map((u) =>
          u.username === selectedUser.username ? { ...u, groups: selectedGroups } : u
        );
        cachedUsers = updated;
        return updated;
      });
      setModalOpen(false);
      toast.success("Grupos atualizados.");
    } catch (err) {
      console.error("Error saving groups:", err);
      setSaveError("Erro ao salvar grupos. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const openInviteModal = () => {
    setInviteEmail("");
    setInviteGivenName("");
    setInviteFamilyName("");
    setInviteGroups([]);
    setInviteError(null);
    setInviteSuccess(null);
    setInviteDelivery("email");
    setInvitePassword(generateTemporaryPassword());
    setInviteHandoff(null);
    setCopied(false);
    setInviteOpen(true);
  };

  const openAccessModal = (user) => {
    setAccessUser(user);
    setAccessPassword(generateTemporaryPassword());
    setAccessError(null);
    setAccessSuccess(null);
    setCopied(false);
    setAccessOpen(true);
  };

  const handleSetTemporaryPassword = async () => {
    if (!meetsPasswordPolicy(accessPassword)) { setAccessError(PASSWORD_POLICY_TEXT); return; }
    try {
      setAccessBusy(true);
      setAccessError(null);
      setupApiAuth(auth);
      const result = await ApiService.setUserTemporaryPassword(accessUser.username, accessPassword);
      setAccessSuccess(result.data?.message || result.message || "Senha temporária definida.");
      toast.success("Senha temporária definida.");
      fetchUsers(true);
    } catch (err) {
      console.error("Error setting temporary password:", err);
      setAccessError(err.response?.data?.message || "Erro ao definir senha temporária.");
    } finally {
      setAccessBusy(false);
    }
  };

  const handleResendInvite = async () => {
    try {
      setAccessBusy(true);
      setAccessError(null);
      setupApiAuth(auth);
      const result = await ApiService.resendUserInvite(accessUser.username);
      setAccessSuccess(result.data?.message || result.message || "Convite reenviado.");
    } catch (err) {
      console.error("Error resending invite:", err);
      setAccessError(err.response?.data?.message || "Erro ao reenviar convite.");
    } finally {
      setAccessBusy(false);
    }
  };

  const handleCopy = async (text) => {
    const ok = await copyToClipboard(text);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2000);
  };

  const handoffText = (email, password) =>
    `Acesso ao ACIG Guapiaçu Cidadão\nSite: ${config.APP_URL}login\nE-mail: ${email}\nSenha temporária: ${password}\nNo primeiro acesso você criará sua própria senha.`;

  const handleInvite = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!inviteEmail.trim()) {
      setInviteError("E-mail é obrigatório.");
      return;
    }
    if (!emailRegex.test(inviteEmail.trim())) {
      setInviteError("Informe um e-mail válido.");
      return;
    }
    if (inviteDelivery === "manual" && !meetsPasswordPolicy(invitePassword)) {
      setInviteError(PASSWORD_POLICY_TEXT);
      return;
    }
    try {
      setInviting(true);
      setInviteError(null);
      setupApiAuth(auth);
      const email = inviteEmail.trim().toLowerCase();
      const result = await ApiService.createUser({
        email,
        givenName: inviteGivenName.trim() || undefined,
        familyName: inviteFamilyName.trim() || undefined,
        groups: inviteGroups.length > 0 ? inviteGroups : undefined,
        deliveryMode: inviteDelivery,
        ...(inviteDelivery === "manual" && { temporaryPassword: invitePassword }),
      });
      setInviteSuccess(result.data?.message || result.message || "Usuário criado com sucesso.");
      toast.success("Usuário criado.");
      if (inviteDelivery === "manual") {
        // Keep the modal open: the admin must copy the password now, it is shown only once.
        setInviteHandoff({ email, password: invitePassword });
        fetchUsers(true);
      } else {
        setInviteOpen(false);
        fetchUsers(true);
      }
    } catch (err) {
      console.error("Error creating user:", err);
      const msg = err.response?.data?.message || "Erro ao criar usuário.";
      setInviteError(msg);
    } finally {
      setInviting(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!filterGroup) return users;
    if (filterGroup === "__none__") return users.filter((u) => !u.groups || u.groups.length === 0);
    return users.filter((u) => u.groups?.includes(filterGroup));
  }, [users, filterGroup]);

  const columns = useMemo(() => [
    {
      header: "E-mail",
      accessorKey: "email",
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ getValue }) => getValue() || "—",
    },
    {
      header: "Grupos",
      accessorKey: "groups",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ getValue }) => {
        const groups = getValue() || [];
        if (groups.length === 0) return <span className="text-muted">—</span>;
        return (
          <div className="d-flex flex-wrap gap-1">
            {groups.map((g) => (
              <Badge key={g} color={GROUP_COLORS[g] || "secondary"}>{translateGroup(g)}</Badge>
            ))}
          </div>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "status",
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ getValue }) => (
        <Badge color={STATUS_COLORS[getValue()] || "secondary"} title={getValue()}>{STATUS_LABELS[getValue()] || getValue()}</Badge>
      ),
    },
    {
      header: "Criado em",
      accessorKey: "createdAt",
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ getValue }) => {
        const d = getValue() ? new Date(getValue()) : null;
        return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("pt-BR") : "—";
      },
    },
    {
      header: "Ações",
      accessorKey: "username",
      id: "actions",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const u = row.original;
        const isSelf = u.username === auth.user?.profile?.sub;
        const isAdminUser = (u.groups || []).includes("admin");
        const pendingInvite = u.status === "FORCE_CHANGE_PASSWORD";
        return (
          <UncontrolledDropdown>
            <DropdownToggle color={pendingInvite ? "warning" : "secondary"} outline size="sm" caret aria-label={`Ações para ${u.email || u.username}`}>
              <i className={`bx ${pendingInvite ? "bx-key" : "bx-cog"} me-1`} aria-hidden="true" />Ações
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => openGroupModal(u)}>
                <i className="bx bx-group me-2" aria-hidden="true" />Grupos
              </DropdownItem>
              <DropdownItem onClick={() => openAccessModal(u)}>
                <i className="bx bx-key me-2" aria-hidden="true" />{pendingInvite ? "Acesso (convite pendente)" : "Acesso"}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem className="text-danger" onClick={() => openDeleteModal(u)} disabled={isSelf || isAdminUser}
                title={isAdminUser ? "Remova do grupo admin antes de excluir" : isSelf ? "Você não pode excluir a própria conta" : undefined}>
                <i className="bx bx-trash me-2" aria-hidden="true" />Excluir
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
  ], [auth.user]);

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <h4 className="mb-0">Usuários <span className="text-muted fw-normal font-size-14">({users.length})</span></h4>
          <div className="d-flex gap-2">
            <Button color="success" size="sm" onClick={openInviteModal}>
              <i className="bx bx-user-plus me-1" />
              Convidar Usuário
            </Button>
            <Button color="primary" outline size="sm" onClick={() => fetchUsers(true)} disabled={loading} aria-label="Atualizar lista">
              <i className={`bx bx-refresh me-1 ${loading ? "bx-spin" : ""}`} />
              Atualizar
            </Button>
          </div>
        </div>

        {loading && users.length === 0 && <div className="text-center py-5"><Spinner color="primary" /></div>}
        {error && <Alert color="danger">{error}</Alert>}
        {!loading && !error && users.length === 0 && <Alert color="info">Nenhum usuário encontrado.</Alert>}

        {!error && users.length > 0 && (
          <>
            <Card className="mb-3">
              <CardBody>
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Label className="form-label">Filtrar por Grupo</Label>
                    <Input type="select" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
                      <option value="">Todos</option>
                      <option value="__none__">Sem grupo</option>
                      {allGroups.map((g) => (
                        <option key={g.name} value={g.name}>{translateGroup(g.name)}</option>
                      ))}
                    </Input>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {filteredUsers.length === 0 ? (
              <Alert color="info">Nenhum usuário encontrado para este filtro.</Alert>
            ) : (
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={filteredUsers}
                isPagination={true}
                isCustomPageSize={true}
                isGlobalFilter={true}
                SearchPlaceholder="Buscar por e-mail..."
                tableClass="table-hover"
                theadClass="table-light"
                paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                pagination="pagination"
              />
            </CardBody>
          </Card>
            )}
          </>
        )}

        {/* Group assignment modal */}
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
          <ModalHeader toggle={() => setModalOpen(false)}>
            Gerenciar Grupos — {selectedUser?.email || selectedUser?.username}
          </ModalHeader>
          <ModalBody>
            {saveError && <Alert color="danger">{saveError}</Alert>}
            {allGroups.length === 0 ? (
              <p className="text-muted">Nenhum grupo disponível.</p>
            ) : (
              allGroups.map((group) => (
                <FormGroup check key={group.name} className="mb-2">
                  <Input
                    type="checkbox"
                    id={`group-${group.name}`}
                    checked={selectedGroups.includes(group.name)}
                    onChange={() => toggleGroup(group.name)}
                  />
                  <Label check for={`group-${group.name}`}>
                    <Badge color={GROUP_COLORS[group.name] || "secondary"} className="me-2">{translateGroup(group.name)}</Badge>
                    {group.description && <small className="text-muted">{group.description}</small>}
                  </Label>
                </FormGroup>
              ))
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setModalOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : null}
              Salvar
            </Button>
          </ModalFooter>
        </Modal>
        {/* Invite user modal */}
        <Modal isOpen={inviteOpen} toggle={() => setInviteOpen(false)}>
          <ModalHeader toggle={() => setInviteOpen(false)}>Convidar Novo Usuário</ModalHeader>
          <ModalBody>
            {inviteError && <Alert color="danger">{inviteError}</Alert>}
            {inviteSuccess && <Alert color="success">{inviteSuccess}</Alert>}
            <FormGroup>
              <Label for="invite-email">E-mail *</Label>
              <Input
                type="email"
                id="invite-email"
                placeholder="usuario@exemplo.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviting || !!inviteSuccess}
              />
            </FormGroup>
            <FormGroup>
              <Label for="invite-given-name">Nome</Label>
              <Input
                type="text"
                id="invite-given-name"
                placeholder="Nome"
                value={inviteGivenName}
                onChange={(e) => setInviteGivenName(e.target.value)}
                disabled={inviting || !!inviteSuccess}
              />
            </FormGroup>
            <FormGroup>
              <Label for="invite-family-name">Sobrenome</Label>
              <Input
                type="text"
                id="invite-family-name"
                placeholder="Sobrenome"
                value={inviteFamilyName}
                onChange={(e) => setInviteFamilyName(e.target.value)}
                disabled={inviting || !!inviteSuccess}
              />
            </FormGroup>
            <FormGroup>
              <Label>Grupos</Label>
              {allGroups.map((group) => (
                <FormGroup check key={`invite-grp-${group.name}`} className="mb-1">
                  <Input
                    type="checkbox"
                    id={`invite-grp-${group.name}`}
                    checked={inviteGroups.includes(group.name)}
                    onChange={() =>
                      setInviteGroups((prev) =>
                        prev.includes(group.name) ? prev.filter((g) => g !== group.name) : [...prev, group.name]
                      )
                    }
                    disabled={inviting || !!inviteSuccess}
                  />
                  <Label check for={`invite-grp-${group.name}`}>
                    <Badge color={GROUP_COLORS[group.name] || "secondary"} className="me-2">{translateGroup(group.name)}</Badge>
                    {group.description && <small className="text-muted">{group.description}</small>}
                  </Label>
                </FormGroup>
              ))}
            </FormGroup>
            <FormGroup>
              <Label>Como entregar o acesso</Label>
              <FormGroup check className="mb-1">
                <Input type="radio" id="invite-delivery-email" name="invite-delivery" value="email"
                  checked={inviteDelivery === "email"} onChange={() => setInviteDelivery("email")} disabled={inviting || !!inviteSuccess} />
                <Label check for="invite-delivery-email">
                  Enviar e-mail com senha temporária
                  <small className="text-muted d-block">Padrão. Alguns provedores bloqueiam ou atrasam o e-mail do Cognito.</small>
                </Label>
              </FormGroup>
              <FormGroup check>
                <Input type="radio" id="invite-delivery-manual" name="invite-delivery" value="manual"
                  checked={inviteDelivery === "manual"} onChange={() => setInviteDelivery("manual")} disabled={inviting || !!inviteSuccess} />
                <Label check for="invite-delivery-manual">
                  Definir senha temporária e informar pessoalmente
                  <small className="text-muted d-block">Nenhum e-mail é enviado. Você passa a senha por telefone ou WhatsApp; a pessoa cria a própria senha no primeiro acesso.</small>
                </Label>
              </FormGroup>
            </FormGroup>

            {inviteDelivery === "manual" && !inviteHandoff && (
              <FormGroup>
                <Label for="invite-password">Senha temporária</Label>
                <div className="input-group">
                  <Input type="text" id="invite-password" value={invitePassword} onChange={(e) => setInvitePassword(e.target.value)}
                    disabled={inviting || !!inviteSuccess} className="font-monospace" autoComplete="off" />
                  <Button color="light" onClick={() => setInvitePassword(generateTemporaryPassword())} disabled={inviting || !!inviteSuccess} title="Gerar outra">
                    <i className="bx bx-refresh" />
                  </Button>
                </div>
                <small className="text-muted">{PASSWORD_POLICY_TEXT}</small>
              </FormGroup>
            )}

            {inviteHandoff && (
              <Card className="border mb-0">
                <CardBody className="p-3">
                  <div className="fw-semibold mb-2"><i className="bx bx-info-circle me-1" />Copie agora, a senha não será exibida de novo</div>
                  <pre className="mb-2 small bg-body-secondary p-2 rounded" style={{ whiteSpace: "pre-wrap" }}>{handoffText(inviteHandoff.email, inviteHandoff.password)}</pre>
                  <Button color="primary" size="sm" onClick={() => handleCopy(handoffText(inviteHandoff.email, inviteHandoff.password))}>
                    <i className={`bx ${copied ? "bx-check" : "bx-copy"} me-1`} />{copied ? "Copiado" : "Copiar mensagem"}
                  </Button>
                </CardBody>
              </Card>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setInviteOpen(false)} disabled={inviting}>
              {inviteHandoff ? "Fechar" : "Cancelar"}
            </Button>
            {!inviteHandoff && (
              <Button color="success" onClick={handleInvite} disabled={inviting || !!inviteSuccess}>
                {inviting ? <Spinner size="sm" className="me-1" /> : null}
                {inviteDelivery === "manual" ? "Criar acesso" : "Convidar"}
              </Button>
            )}
          </ModalFooter>
        </Modal>

        {/* Access modal — temporary password / resend invite */}
        <Modal isOpen={accessOpen} toggle={() => setAccessOpen(false)}>
          <ModalHeader toggle={() => setAccessOpen(false)}>
            Acesso — {accessUser?.email || accessUser?.username}
          </ModalHeader>
          <ModalBody>
            {accessError && <Alert color="danger">{accessError}</Alert>}
            {accessSuccess && <Alert color="success">{accessSuccess}</Alert>}

            <p className="text-muted small mb-3">
              Status atual: <Badge color={STATUS_COLORS[accessUser?.status] || "secondary"}>{STATUS_LABELS[accessUser?.status] || accessUser?.status}</Badge>
            </p>

            <h6>Definir senha temporária</h6>
            <p className="text-muted small">
              Use quando o e-mail do Cognito não chegou. Nenhum e-mail é enviado; você informa a senha à pessoa, e ela cria a própria senha no primeiro acesso. A senha atual dela deixa de valer.
            </p>
            <FormGroup>
              <div className="input-group">
                <Input type="text" value={accessPassword} onChange={(e) => setAccessPassword(e.target.value)} disabled={accessBusy || !!accessSuccess}
                  className="font-monospace" autoComplete="off" />
                <Button color="light" onClick={() => setAccessPassword(generateTemporaryPassword())} disabled={accessBusy || !!accessSuccess} title="Gerar outra">
                  <i className="bx bx-refresh" />
                </Button>
              </div>
              <small className="text-muted">{PASSWORD_POLICY_TEXT}</small>
            </FormGroup>
            <div className="d-flex gap-2 mb-4">
              <Button color="warning" onClick={handleSetTemporaryPassword} disabled={accessBusy || !!accessSuccess}>
                {accessBusy ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-key me-1" />}
                Definir senha temporária
              </Button>
              {accessSuccess && accessUser && (
                <Button color="primary" outline onClick={() => handleCopy(handoffText(accessUser.email, accessPassword))}>
                  <i className={`bx ${copied ? "bx-check" : "bx-copy"} me-1`} />{copied ? "Copiado" : "Copiar mensagem"}
                </Button>
              )}
            </div>

            {accessUser?.status === "FORCE_CHANGE_PASSWORD" && (
              <>
                <h6>Reenviar convite por e-mail</h6>
                <p className="text-muted small">Gera uma nova senha temporária e reenvia o e-mail padrão do Cognito.</p>
                <Button color="secondary" outline onClick={handleResendInvite} disabled={accessBusy || !!accessSuccess}>
                  <i className="bx bx-mail-send me-1" />Reenviar convite
                </Button>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setAccessOpen(false)} disabled={accessBusy}>Fechar</Button>
          </ModalFooter>
        </Modal>

        {/* Delete user modal */}
        <Modal isOpen={deleteOpen} toggle={() => !deleting && setDeleteOpen(false)}>
          <ModalHeader toggle={() => !deleting && setDeleteOpen(false)}>Excluir usuário</ModalHeader>
          <ModalBody>
            {deleteError && <Alert color="danger">{deleteError}</Alert>}
            <p>
              Você está prestes a excluir <strong>{deleteUser?.email || deleteUser?.username}</strong>.
            </p>
            <p className="text-danger mb-3">
              A conta de acesso e todos os dados dessa pessoa (perfil, currículo, experiências e competências) serão removidos. Esta ação não pode ser desfeita. Um registro de auditoria será mantido.
            </p>
            <FormGroup>
              <Label for="delete-confirm">Para confirmar, digite o e-mail do usuário</Label>
              <Input id="delete-confirm" type="text" autoComplete="off" value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder={deleteUser?.email || ""} disabled={deleting} />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancelar</Button>
            <Button color="danger" onClick={handleDeleteUser}
              disabled={deleting || !deleteUser?.email || deleteConfirmText.trim().toLowerCase() !== (deleteUser?.email || "").toLowerCase()}>
              {deleting ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-trash me-1" />}
              Excluir definitivamente
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default UsersList;
